// @ts-check
import React, { useCallback, useMemo, useState } from 'react';
import { ButtonVariant, Flex, FlexItem } from '@patternfly/react-core';
import PropTypes from 'prop-types';

import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import { useTranslation } from 'localization/useTranslation';
import T from 'localization/LocalizationKeys';
import _ from 'lodash';
import SelectResourcesModal from 'components/Modals/SelectResourcesModal';
import useBoolean from 'utils/hooks/useBoolean';
import RelationDropdown from './RelationDropdown';
import { OrgAccessLevelMap, NoRelation, GroupRelationMap } from './RelationRadios';

/**
 * @enum {string}
 */
export const VisibleColumns = Object.freeze({
  displayName: 'displayName',
  type: 'type',
  relation: 'relation',
  id: 'id',
  provider: 'provider'
});

/**
 * @typedef {object} ResourceType
 * @prop {string} id
 * @prop {string} displayName
 * @prop {string} type
 * @prop {string} relation
 * @prop {string} provider
 */

/**
 * @type {(import('components/Common/SimpleTable/SimpleTable').ColumnType<ResourceType> & { columnKey: string })[]}
 */
const defaultTableColumns = [
  {
    columnKey: VisibleColumns.displayName,
    label: T.common.displayname,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    columnKey: VisibleColumns.id,
    label: T.common.id,
    path: 'id',
    isSortable: true
  },
  {
    columnKey: VisibleColumns.type,
    label: T.common.type,
    path: 'type',
    isSortable: true
  },
  {
    columnKey: VisibleColumns.provider,
    label: T.common.providers,
    path: 'provider',
    isSortable: true
  }
];

export const createRelation = (orgId, id, resourceType, relation) => {
  return {
    resourceType: resourceType,
    resourceId: id,
    relation: relation,
    subjectType: 'organization',
    subjectId: orgId
  };
};

/**
 * @type {import('components/Common/SimpleTable/SimpleTable').SortByType<ResourceType>}
 * @constant
 */
const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};


/**
 * @typedef {object} RelationTableProps
 * @prop {string} orgId - the id of the organization
 * @prop {VisibleColumns[]} visibleColumns - the columns to display
 * @prop {Array} resources - the resources to display
 * @prop {boolean} [isLoading=false] - if the table is loading
 * @prop {function} updateRelationsFn - the function to update relations
 * @prop {function} deleteRelationsFn - the function to delete relations
 * @prop {import('components/Common/SimpleTable/SimpleTable').ActionType<ResourceType>[]} [prependedRowActions=[]] - the actions to prepend to the row actions
 */

/**
 * @description table to display relations between an organization and a resource
 * @param {RelationTableProps} props
 * @returns {JSX.Element}
 */
const RelationTable = ({
  orgId,
  visibleColumns = [VisibleColumns.displayName, VisibleColumns.type, VisibleColumns.relation],
  resources, isLoading, updateRelationsFn, deleteRelationsFn, prependedRowActions = [] }) => {

  const T = useTranslation();

  const [selected, available] = _.partition(resources, resource => resource.relation !== NoRelation);
  const [isShowingSelection, , closeSelection, toggleSelection] = useBoolean(false);

  const onRelationChanged = useCallback((relation, row) => {
    const newRelationObj = createRelation(orgId, row.id, row.type, relation);
    updateRelationsFn([newRelationObj]);
  }, [orgId, updateRelationsFn]);

  const columns = useMemo(() => {
    /** @type {(import('components/Common/SimpleTable/SimpleTable').ColumnType<ResourceType> & { columnKey: string })[]} */
    const allColumns = [
      ...defaultTableColumns,
      {
        columnKey: VisibleColumns.relation,
        label: T.common.relation,
        path: 'relation',
        isSortable: false,
        searchItem: true,
        render: (relation, row) => {
          return (
            <>
              <Flex>
                <FlexItem>
                  <RelationDropdown
                    relation={relation}
                    relationsMap={OrgAccessLevelMap}
                    onRelationChanged={(relation) => onRelationChanged(relation, row)} />
                </FlexItem>
              </Flex>
            </>
          );
        }
      }
    ];
    return allColumns.filter(column => _.some(visibleColumns, visibleKey => visibleKey === column.columnKey));
  }, [T.common.relation, onRelationChanged, visibleColumns]);

  /**
   * @type {[string[], function(string[]): void]}
   */
  const [relationFilter, setRelationFilter] = useState([]);

  const globalActions = useMemo(() => {
    return [
      {
        render: () => (
          <RelationDropdown
            relationsMap={GroupRelationMap}
            relation={relationFilter}
            onRelationChanged={setRelationFilter}
            placeholder={T.common.relations}
            isMultiple />
        )
      },
      {
        label: T.common.add,
        onClick: () => {
          toggleSelection();
        }
      }
    ];
  }, [T.common.add, T.common.relations, relationFilter, toggleSelection]);

  const rowActions = useMemo(/** @returns {import('components/Common/SimpleTable/SimpleTable').ActionType<ResourceType>[]} */() => {
    return [
      ...prependedRowActions,
      {
        variant: ButtonVariant.danger,
        isOverflow: true,
        title: T.common.delete,
        onClick: (row) => {
          const relation = createRelation(orgId, row.id, row.type, row.relation);
          deleteRelationsFn(relation);
        }
      }
    ];
  }, [T.common.delete, deleteRelationsFn, orgId, prependedRowActions]);

  const filtered = useMemo(() => {
    if (_.isEmpty(relationFilter)) {
      return selected;
    }

    return _.filter(selected, item => {
      return _.some(relationFilter, relation => relation === item.relation);
    });
  }, [relationFilter, selected]);

  return (
    <>
      <SimpleTable
        globalActions={globalActions}
        actions={rowActions}
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        sortBy={DefaultSortBy}
        compact
        usePaging
      />
      <SelectResourcesModal
        title={T.common.add}
        tableColumns={defaultTableColumns}
        isOpen={isShowingSelection}
        onClose={closeSelection}
        available={available}
        orgId={orgId}
        updateRelationsFn={updateRelationsFn}
      />
    </>
  );
};
RelationTable.propTypes = {
  orgId: PropTypes.string.isRequired,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  resources: PropTypes.array,
  isLoading: PropTypes.bool,
  updateRelationsFn: PropTypes.func,
  deleteRelationsFn: PropTypes.func
};

export default RelationTable;
